/**
 * Margins (§8.1): lender list with current Jax/Davidson/BP-only/Max Comp,
 * inline alias management, effective-dated margin versions behind ONE
 * "Edit" flow (margin changes create a new version — same-day edits
 * supersede; lender identity updates in place), version History drawer,
 * and the D-38 "Diff vs pipeline Margins tab" tool (reuses the importer's
 * stage diff, margins section only — read-only, nothing applied from here).
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { payrollApi } from '../api';
import { EditorFields, coerceNumericFields } from './EntityManager';
import { marginVersionFields } from './entityConfigs';

const todayIso = () => new Date().toISOString().slice(0, 10);
const current = (l) => (l.versions && l.versions.length ? l.versions[0] : null);
const isSuperseded = (v) => !!v.effective_end && String(v.effective_end) < String(v.effective_start);

// Lender identity (edit mode only — create collects name + first margins).
// lender_notes is a distinct form key: the version table also has "notes".
const LENDER_FIELDS = [
  { key: 'canonical_name', label: 'Canonical name', required: true, help: 'Pipeline Lender/Investor cells match this (lowercased) or an alias.' },
  { key: 'is_active', label: 'Active', type: 'boolean', checkboxLabel: 'Lender is active' },
  { key: 'lender_notes', label: 'Lender notes', type: 'textarea' },
];

// Version fields minus the two we render as dedicated controls at the bottom.
const MARGIN_RATE_FIELDS = marginVersionFields.filter((f) => f.key !== 'effective_start' && f.key !== 'notes');

/** Type-aware value normalization so "2.375" (string) == 2.375 (number). */
const norm = (field, v) => {
  if (field.type === 'number') return (v === '' || v === null || v === undefined) ? null : Number(v);
  if (field.type === 'boolean') return !!v;
  return v === undefined || v === null ? '' : String(v).trim();
};

const fieldsChanged = (fields, form, original) => fields.some((f) => norm(f, form[f.key]) !== norm(f, (original || {})[f.key]));

const marginCell = (v, key) => {
  if (!v) return '—';
  if (v.bp_only) return 'BP';
  return v[key] === null || v[key] === undefined ? '—' : `${v[key]}%`;
};

const marginSummary = (v) => (v.bp_only
  ? 'BP only'
  : `Jax ${v.jax_pct ?? '—'}% · Davidson ${v.davidson_pct ?? '—'}%${v.max_comp != null ? ` · Max Comp $${Number(v.max_comp).toLocaleString()}` : ''}`);

export default function MarginsManager({ isSuperAdmin }) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const sectionColor = useColorModeValue('gray.600', 'gray.400');
  const cancelRef = useRef();

  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');

  // editor: { mode: 'create'|'edit', lender?, version?, form, effectiveDate, reason }
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aliasFor, setAliasFor] = useState(null);
  const [newAlias, setNewAlias] = useState('');
  const [aliasBusy, setAliasBusy] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [diff, setDiff] = useState(null); // { rows, warnings, at }
  const [diffBusy, setDiffBusy] = useState(false);

  const load = async () => {
    setLoadError(null);
    try {
      const { rows: data } = await payrollApi.manageReference({ entity: 'lenders', action: 'list' });
      setRows(data);
      if (aliasFor) setAliasFor(data.find((l) => l.id === aliasFor.id) || null);
    } catch (err) {
      setLoadError(err.message);
    }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((l) => l.canonical_name.toLowerCase().includes(q)
      || (l.aliases || []).some((a) => a.alias.toLowerCase().includes(q)));
  }, [rows, search]);

  const openCreate = () => setEditor({ mode: 'create', form: {}, effectiveDate: todayIso(), reason: '' });

  const openEdit = (l) => {
    const v = current(l) || {};
    const form = { canonical_name: l.canonical_name, is_active: l.is_active, lender_notes: l.notes };
    MARGIN_RATE_FIELDS.forEach((f) => { form[f.key] = v[f.key]; });
    setEditor({ mode: 'edit', lender: l, version: v, form, effectiveDate: todayIso(), reason: '' });
  };

  const lenderOriginal = editor && editor.mode === 'edit' && editor.lender
    ? { canonical_name: editor.lender.canonical_name, is_active: editor.lender.is_active, lender_notes: editor.lender.notes }
    : null;
  const lenderChanged = editor && editor.mode === 'edit' && fieldsChanged(LENDER_FIELDS, editor.form, lenderOriginal);
  const marginsChanged = editor && (editor.mode === 'create' || fieldsChanged(MARGIN_RATE_FIELDS, editor.form, editor.version));

  const save = async () => {
    const { mode, lender, form, effectiveDate, reason } = editor;
    const { values, badFields } = coerceNumericFields(MARGIN_RATE_FIELDS, form);
    if (badFields.length) {
      toast({ title: `Not a number: ${badFields.join(', ')}`, status: 'warning', duration: 4000 });
      return;
    }
    if ((mode === 'create' || marginsChanged) && !effectiveDate) {
      toast({ title: 'Effective date is required', status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        if (!values.canonical_name) throw new Error('Canonical name is required.');
        await payrollApi.manageReference({
          entity: 'lenders',
          action: 'create',
          payload: { ...values, effective_start: effectiveDate, notes: reason || null },
          reason: reason || undefined,
        });
      } else {
        if (!lenderChanged && !marginsChanged) {
          toast({ title: 'No changes to save', status: 'info', duration: 3000 });
          setSaving(false);
          return;
        }
        if (lenderChanged) {
          await payrollApi.manageReference({
            entity: 'lenders',
            action: 'update',
            id: lender.id,
            payload: { canonical_name: values.canonical_name, is_active: values.is_active !== false, notes: values.lender_notes || null },
            reason: reason || undefined,
          });
        }
        if (marginsChanged) {
          const ratePayload = {};
          MARGIN_RATE_FIELDS.forEach((f) => { ratePayload[f.key] = values[f.key] === undefined ? null : values[f.key]; });
          ratePayload.effective_start = effectiveDate;
          ratePayload.notes = reason || null;
          await payrollApi.manageReference({
            entity: 'lenders', action: 'addVersion', id: lender.id, payload: ratePayload, reason: reason || undefined,
          });
        }
      }
      toast({ title: 'Saved', status: 'success', duration: 3000 });
      setEditor(null);
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const addAlias = async () => {
    const alias = newAlias.trim().toLowerCase();
    if (!alias) return;
    setAliasBusy(true);
    try {
      await payrollApi.manageReference({
        entity: 'lenderAliases', action: 'create',
        payload: { alias, lender_id: aliasFor.id, active: true },
        reason: `alias for ${aliasFor.canonical_name}`,
      });
      setNewAlias('');
      await load();
    } catch (err) {
      toast({ title: 'Alias failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setAliasBusy(false);
    }
  };

  const removeAlias = async (a) => {
    try {
      await payrollApi.manageReference({ entity: 'lenderAliases', action: 'delete', id: a.id, reason: 'removed via Margins UI' });
      await load();
    } catch (err) {
      toast({ title: 'Remove failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await payrollApi.manageReference({ entity: 'lenders', action: 'delete', id: deleting.id, reason: 'deleted via Margins UI' });
      toast({ title: 'Deleted', status: 'success', duration: 3000 });
      setDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setDeleteBusy(false);
    }
  };

  const runDiff = async () => {
    setDiffBusy(true);
    try {
      const { diff: d } = await payrollApi.importReference({ action: 'stage' });
      setDiff({ rows: d.margins || [], warnings: d.marginWarnings || [], at: new Date() });
    } catch (err) {
      toast({ title: 'Diff failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setDiffBusy(false);
    }
  };

  if (loadError) return <Alert status="error" borderRadius="md"><AlertIcon />{loadError}<Button ml={3} size="sm" onClick={load}>Retry</Button></Alert>;
  if (!rows) return <Flex justify="center" py={10}><Spinner /></Flex>;

  const editorTitle = editor && (editor.mode === 'create'
    ? 'New lender'
    : `Edit — ${editor.lender && editor.lender.canonical_name}`);

  return (
    <Box>
      <Flex gap={2} mb={3} wrap="wrap" align="center">
        <Input size="sm" maxW="260px" placeholder="Search lender or alias…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Box flex="1" />
        <Button size="sm" variant="outline" onClick={runDiff} isLoading={diffBusy} loadingText="Reading sheet…">
          Diff vs pipeline Margins tab
        </Button>
        <Button size="sm" colorScheme="purple" onClick={openCreate}>New lender</Button>
      </Flex>

      {diff && (
        <Box borderWidth="1px" borderRadius="md" p={3} mb={3}>
          <Flex justify="space-between" align="baseline" mb={2}>
            <Text fontWeight="semibold" fontSize="sm">
              Sheet vs Hub diff — {diff.rows.filter((r) => r.status === 'changed').length} changed,{' '}
              {diff.rows.filter((r) => r.status === 'new').length} sheet-only,{' '}
              {diff.rows.filter((r) => r.status === 'unchanged').length} unchanged
            </Text>
            <Button size="xs" variant="ghost" onClick={() => setDiff(null)}>dismiss</Button>
          </Flex>
          {diff.rows.filter((r) => r.status !== 'unchanged').length === 0 && (
            <Text fontSize="sm" color="green.500">No drift — the Hub matches the sheet.</Text>
          )}
          {diff.rows.filter((r) => r.status !== 'unchanged').map((r) => (
            <Text key={r.lender} fontSize="xs" py={0.5}>
              <Badge mr={2} colorScheme={r.status === 'new' ? 'green' : 'orange'}>{r.status}</Badge>
              <b>{r.lender}</b>
              {(r.changes || []).map((c) => (
                <Code key={c.field} fontSize="xs" ml={2}>{c.field}: {String(c.db ?? '—')} → {String(c.staged ?? '—')}</Code>
              ))}
            </Text>
          ))}
          {diff.warnings.length > 0 && (
            <Text fontSize="xs" color="orange.400" mt={1}>{diff.warnings.join(' · ')}</Text>
          )}
          <Text fontSize="xs" color="gray.500" mt={2}>
            Read-only comparison. Apply sheet changes through the Import tab, or hand-edit here (Edit
            creates a new margin version).
          </Text>
        </Box>
      )}

      <Box overflowX="auto" borderWidth="1px" borderRadius="md">
        <Table size="sm">
          <Thead>
            <Tr><Th>Lender</Th><Th>Jax</Th><Th>Davidson</Th><Th isNumeric>Max Comp</Th><Th>Aliases</Th><Th>Since</Th><Th /></Tr>
          </Thead>
          <Tbody>
            {filtered.map((l) => {
              const v = current(l);
              return (
                <Tr key={l.id} _hover={{ bg: hoverBg }} opacity={l.is_active ? 1 : 0.5}>
                  <Td whiteSpace="nowrap">{l.canonical_name}{!l.is_active && <Badge ml={1}>inactive</Badge>}</Td>
                  <Td>{marginCell(v, 'jax_pct')}</Td>
                  <Td>{marginCell(v, 'davidson_pct')}</Td>
                  <Td isNumeric>{v && v.max_comp != null ? `$${Number(v.max_comp).toLocaleString()}` : '—'}</Td>
                  <Td>
                    <Flex gap={1} wrap="wrap" maxW="220px">
                      {(l.aliases || []).map((a) => <Tag key={a.id} size="sm"><TagLabel>{a.alias}</TagLabel></Tag>)}
                    </Flex>
                  </Td>
                  <Td fontSize="xs" whiteSpace="nowrap">{v ? v.effective_start : '—'}</Td>
                  <Td textAlign="right" whiteSpace="nowrap">
                    <Button size="xs" variant="outline" mr={1} onClick={() => openEdit(l)}>Edit</Button>
                    <Button size="xs" variant="ghost" mr={1} onClick={() => setAliasFor(l)}>Aliases</Button>
                    <Button size="xs" variant="ghost" mr={1} onClick={() => setHistoryFor(l)}>History</Button>
                    {isSuperAdmin && (
                      <Button size="xs" colorScheme="red" variant="ghost" onClick={() => setDeleting(l)}>Delete</Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* editor */}
      <Modal isOpen={!!editor} onClose={() => setEditor(null)} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editorTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editor && editor.mode === 'create' && (
              <EditorFields
                fields={[{ key: 'canonical_name', label: 'Canonical name', required: true }, ...MARGIN_RATE_FIELDS]}
                form={editor.form}
                setForm={(fn) => setEditor((s) => ({ ...s, form: typeof fn === 'function' ? fn(s.form) : fn }))}
              />
            )}
            {editor && editor.mode === 'edit' && (
              <>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={sectionColor} mb={2}>Lender</Text>
                <EditorFields fields={LENDER_FIELDS} form={editor.form} setForm={(fn) => setEditor((s) => ({ ...s, form: typeof fn === 'function' ? fn(s.form) : fn }))} />
                <Divider my={4} />
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={sectionColor} mb={2}>Margins</Text>
                <EditorFields fields={MARGIN_RATE_FIELDS} form={editor.form} setForm={(fn) => setEditor((s) => ({ ...s, form: typeof fn === 'function' ? fn(s.form) : fn }))} />
                <Divider my={4} />
                {marginsChanged && (
                  <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    Margin fields changed — saving creates a new version effective on the date below
                    and closes the current one. The full timeline stays in History.
                  </Alert>
                )}
              </>
            )}
            {editor && (editor.mode === 'create' || marginsChanged) && (
              <FormControl mb={3} isRequired>
                <FormLabel fontSize="sm">{editor.mode === 'create' ? 'First version effective date' : 'New version effective date'}</FormLabel>
                <Input
                  size="sm" type="date" maxW="200px"
                  value={editor.effectiveDate}
                  min={editor.mode === 'edit' && editor.version ? editor.version.effective_start : undefined}
                  onChange={(e) => setEditor((s) => ({ ...s, effectiveDate: e.target.value }))}
                />
                {editor.mode === 'edit' && editor.version && editor.effectiveDate === editor.version.effective_start && (
                  <Text fontSize="xs" color="orange.400" mt={1}>
                    Same day as the current version — it will be superseded (kept in History, never used by runs).
                  </Text>
                )}
              </FormControl>
            )}
            {editor && (
              <FormControl>
                <FormLabel fontSize="sm">Reason / notes (optional, shown in History and the audit log)</FormLabel>
                <Textarea size="sm" rows={2} value={editor.reason}
                  placeholder="What changed and why…"
                  onChange={(e) => setEditor((s) => ({ ...s, reason: e.target.value }))} />
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setEditor(null)}>Cancel</Button>
            <Button colorScheme="purple" onClick={save} isLoading={saving}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* aliases modal */}
      <Modal isOpen={!!aliasFor} onClose={() => setAliasFor(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Aliases — {aliasFor && aliasFor.canonical_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="xs" color="gray.500" mb={2}>
              Lowercase spelling variants from the pipeline Lender/Investor columns. NOTE: at engine
              parity the alias table is intentionally empty (D-26) — adding aliases changes
              margin-row matching and belongs to a signed-off fix version.
            </Text>
            <Flex gap={1} wrap="wrap" mb={3}>
              {aliasFor && (aliasFor.aliases || []).map((a) => (
                <Tag key={a.id} size="md">
                  <TagLabel>{a.alias}</TagLabel>
                  <TagCloseButton onClick={() => removeAlias(a)} />
                </Tag>
              ))}
              {aliasFor && (aliasFor.aliases || []).length === 0 && <Text fontSize="sm" color="gray.500">none</Text>}
            </Flex>
            <Flex gap={2}>
              <Input size="sm" placeholder="new alias (e.g. a&d)" value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addAlias(); }} />
              <Button size="sm" onClick={addAlias} isLoading={aliasBusy}>Add</Button>
            </Flex>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>

      {/* history drawer */}
      <Drawer isOpen={!!historyFor} onClose={() => setHistoryFor(null)} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Margin history — {historyFor && historyFor.canonical_name}</DrawerHeader>
          <DrawerBody>
            {historyFor && (historyFor.versions || []).map((v) => (
              <Box key={v.id} borderWidth="1px" borderRadius="md" p={3} mb={3} opacity={isSuperseded(v) ? 0.6 : 1}>
                <Flex justify="space-between" mb={1}>
                  {isSuperseded(v) ? (
                    <Badge colorScheme="gray">superseded (was effective {v.effective_start})</Badge>
                  ) : (
                    <Badge colorScheme={!v.effective_end ? 'green' : 'gray'}>
                      {v.effective_start} → {v.effective_end || 'open'}
                    </Badge>
                  )}
                </Flex>
                <Text fontSize="xs"><Code fontSize="xs">{marginSummary(v)}</Code></Text>
                {v.notes && <Text fontSize="xs" mt={1}>{v.notes}</Text>}
              </Box>
            ))}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* delete confirm */}
      <AlertDialog isOpen={!!deleting} leastDestructiveRef={cancelRef} onClose={() => setDeleting(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete lender?</AlertDialogHeader>
            <AlertDialogBody>
              Deletes {deleting && deleting.canonical_name}, all margin versions, and its aliases.
              Rows with this lender will flag LENDER_NOT_IN_MARGIN_TAB and price as BP. Prefer
              marking inactive via Edit.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleting(null)}>Cancel</Button>
              <Button colorScheme="red" ml={2} onClick={confirmDelete} isLoading={deleteBusy}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
